/**
 * Frontend logger utility
 * - Development: Logs to console for debugging
 * - Production: Silences logs but tracks errors (can integrate with Sentry/LogRocket)
 * 
 * Usage:
 *   import logger from '@/utils/logger';
 *   logger.log('Debug info');
 *   logger.error('Error occurred', error);
 */

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;

/**
 * Send error to tracking service in production
 * @param {string} level - Error level (error, warn, info)
 * @param {Array} args - Arguments to log
 */
const trackError = (level, args) => {
  // In production, you can integrate with error tracking services here
  // Examples:
  // - Sentry: Sentry.captureException(args[0]);
  // - LogRocket: LogRocket.captureException(args[0]);
  // - Custom API: fetch('/api/logs', { method: 'POST', body: JSON.stringify({ level, message: args }) });
  
  // For now, we'll just silently handle errors in production
  // Uncomment below to see errors in production (not recommended for end users)
  // if (level === 'error') {
  //   console.error('[PROD ERROR]', ...args);
  // }
};

/**
 * Format log message with timestamp and context
 * @param {string} level - Log level
 * @param {Array} args - Arguments to log
 */
const formatLog = (level, args) => {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
  return [prefix, ...args];
};

const logger = {
  /**
   * General logging - development only
   */
  log: (...args) => {
    if (isDev) {
      console.log(...formatLog('log', args));
    }
  },
  
  /**
   * Info logging - development only
   */
  info: (...args) => {
    if (isDev) {
      console.info(...formatLog('info', args));
    } else if (isProd) {
      trackError('info', args);
    }
  },
  
  /**
   * Warning logging - development + production tracking
   */
  warn: (...args) => {
    if (isDev) {
      console.warn(...formatLog('warn', args));
    } else if (isProd) {
      trackError('warn', args);
    }
  },
  
  /**
   * Error logging - development + production tracking
   * In production, errors are tracked but not shown to users
   */
  error: (...args) => {
    if (isDev) {
      console.error(...formatLog('error', args));
    } else if (isProd) {
      trackError('error', args);
    }
  },
  
  /**
   * Debug logging - development only
   */
  debug: (...args) => {
    if (isDev) {
      console.debug(...formatLog('debug', args));
    }
  },
  
  /**
   * Table logging - development only
   */
  table: (...args) => {
    if (isDev) {
      console.table(...args);
    }
  },

  /**
   * Group logging - development only
   */
  group: (label) => {
    if (isDev) {
      console.group(label);
    }
  },

  /**
   * Group end - development only
   */
  groupEnd: () => {
    if (isDev) {
      console.groupEnd();
    }
  },

  /**
   * Time tracking - development only
   */
  time: (label) => {
    if (isDev) {
      console.time(label);
    }
  },

  /**
   * Time tracking end - development only
   */
  timeEnd: (label) => {
    if (isDev) {
      console.timeEnd(label);
    }
  }
};

export default logger;
